// data/unlocks.ts — the Ember Shrine tree and the Provisioner's stock (DESIGN §7.1/§7.2).
// DATA ONLY: every entry is a typed UnlockNode whose `effect` is a registered
// meta-effect key. The implementations live in game/meta.ts (EFFECTS), the same
// way data/relics.ts points at hooks in game/relics.ts.
//
// DESIGN §7.1 is law on costs. Do not sand them off.
//
// PAIRS. types.ts models an either/or pair as two nodes that name each other in
// `pair`. DESIGN counts the pair as ONE numbered node, which is why the list
// below has 22 entries for "18 nodes": rows 2/3/4/5 each carry one pair. Both
// sides can be owned (Mirror model: independent ranks, free swap at the hub);
// only the side named by SaveData.shrineToggles[pairId] is active in a run.
// `params.pairId` is the toggle key, `params.side` is which half this node is.

import type { UnlockNode } from '../types'

export const SHRINE_NODES: UnlockNode[] = [
  // ------------------------------------------------------------ ROW 1 (open)
  {
    id: 'vigor',
    system: 'shrine',
    row: 1,
    name: 'Vigor',
    desc: '+10 max HP.',
    ranks: 3,
    costs: [25, 50, 100],
    effect: 'maxHpAdd',
    params: { add: 10 },
  },
  {
    id: 'edge',
    system: 'shrine',
    row: 1,
    name: 'Edge',
    desc: '+6% damage.',
    ranks: 3,
    costs: [25, 50, 100],
    effect: 'damageMul',
    params: { perRank: 0.06 },
  },
  {
    id: 'fleetfoot',
    system: 'shrine',
    row: 1,
    name: 'Fleetfoot',
    desc: '+4% movement speed.',
    ranks: 2,
    costs: [40, 80],
    effect: 'speedMul',
    params: { perRank: 0.04 },
  },
  {
    id: 'wayfare',
    system: 'shrine',
    row: 1,
    name: 'Wayfare',
    desc: 'Begin each descent holding one Emberburst.',
    ranks: 1,
    costs: [60],
    effect: 'startPotions',
    params: { add: 1 },
  },

  // -------------------------------------------------------- ROW 2 (reach Z2)
  {
    id: 'deep_pockets',
    system: 'shrine',
    row: 2,
    name: 'Deep Pockets',
    desc: 'Start each descent with 60 gold.',
    ranks: 1,
    costs: [75],
    pair: 'ember_sense',
    effect: 'startGold',
    params: { add: 60, pairId: 'row2_purse', side: 'a' },
  },
  {
    id: 'ember_sense',
    system: 'shrine',
    row: 2,
    name: 'Ember Sense',
    desc: '+15% embers from everything.',
    ranks: 1,
    costs: [75],
    pair: 'deep_pockets',
    effect: 'emberMul',
    params: { add: 0.15, pairId: 'row2_purse', side: 'b' },
  },
  {
    id: 'second_wind',
    system: 'shrine',
    row: 2,
    name: 'Second Wind',
    desc: '+20 HP restored on every boss kill.',
    ranks: 1,
    costs: [90],
    effect: 'secondWind',
    params: { heal: 20 },
  },
  {
    id: 'twin_step',
    system: 'shrine',
    row: 2,
    name: 'Twin Step',
    desc: '+1 dash charge. Movement is never the same again.',
    ranks: 1,
    costs: [150],
    effect: 'dashChargeAdd',
    params: { add: 1 },
  },

  // -------------------------------------------------------- ROW 3 (reach Z3)
  {
    id: 'deaths_refusal',
    system: 'shrine',
    row: 3,
    name: "Death's Refusal",
    desc: 'Once per run, rise again at half health.',
    ranks: 1,
    costs: [200],
    pair: 'stubborn_cinder',
    effect: 'revive',
    params: { charges: 1, hpFrac: 0.5, pairId: 'row3_mercy', side: 'a' },
  },
  {
    id: 'stubborn_cinder',
    system: 'shrine',
    row: 3,
    name: 'Stubborn Cinder',
    desc: '+1 HP per second while out of combat.',
    ranks: 1,
    costs: [200],
    pair: 'deaths_refusal',
    effect: 'regen',
    params: { perSec: 1, pairId: 'row3_mercy', side: 'b' },
  },
  {
    id: 'vault_sense',
    system: 'shrine',
    row: 3,
    name: 'Vault Sense',
    desc: 'Timed vaults stay open 20s longer.',
    ranks: 1,
    costs: [110],
    effect: 'vaultClockAdd',
    params: { seconds: 20 },
  },
  {
    id: 'potion_belt',
    system: 'shrine',
    row: 3,
    name: 'Potion Belt',
    desc: '+1 Emberburst carried.',
    ranks: 2,
    costs: [80, 160],
    effect: 'potionCapAdd',
    params: { add: 1 },
  },

  // -------------------------------------------------------- ROW 4 (reach Z4)
  {
    id: 'lampwright',
    system: 'shrine',
    row: 4,
    name: 'Lampwright',
    desc: 'Lamp radius +25% in the dark places.',
    ranks: 1,
    costs: [130],
    pair: 'gloompact',
    effect: 'lampRadiusMul',
    params: { mul: 1.25, pairId: 'row4_dark', side: 'a' },
  },
  {
    id: 'gloompact',
    system: 'shrine',
    row: 4,
    name: 'Gloompact',
    desc: 'The Gloom fills 15% slower.',
    ranks: 1,
    costs: [130],
    pair: 'lampwright',
    effect: 'gloomRateMul',
    params: { mul: 0.85, pairId: 'row4_dark', side: 'b' },
  },
  {
    id: 'curator',
    system: 'shrine',
    row: 4,
    name: 'Curator',
    desc: 'Respite altars offer three relics instead of two.',
    ranks: 1,
    costs: [140],
    effect: 'curator',
  },
  {
    id: 'deft_hand',
    system: 'shrine',
    row: 4,
    name: 'Deft Hand',
    desc: 'Special cooldown −15%.',
    ranks: 1,
    costs: [160],
    effect: 'specialCooldownMul',
    params: { mul: 0.85 },
  },

  // -------------------------------------------------------- ROW 5 (reach Z5)
  {
    id: 'tollbreaker',
    system: 'shrine',
    row: 5,
    name: 'Tollbreaker',
    desc: 'The first toll door of each zone opens free.',
    ranks: 1,
    costs: [180],
    pair: 'silver_tongue',
    effect: 'tollbreaker',
    params: { pairId: 'row5_purse', side: 'a' },
  },
  {
    id: 'silver_tongue',
    system: 'shrine',
    row: 5,
    name: 'Silver Tongue',
    desc: 'Altar and coffer prices −20%.',
    ranks: 1,
    costs: [180],
    pair: 'tollbreaker',
    effect: 'altarDiscountMul',
    params: { mul: 0.8, pairId: 'row5_purse', side: 'b' },
  },
  {
    id: 'ember_font',
    system: 'shrine',
    row: 5,
    name: 'Ember Font',
    desc: '+25 embers for every boss put down.',
    ranks: 1,
    costs: [200],
    effect: 'emberFontPerBoss',
    params: { add: 25 },
  },
  {
    id: 'iron_ration',
    system: 'shrine',
    row: 5,
    name: 'Iron Ration',
    desc: 'Food heals +10.',
    ranks: 1,
    costs: [120],
    effect: 'foodHealAdd',
    params: { add: 10 },
  },

  // -------------------------------------------------------- ROW 6 (reach Z6)
  {
    id: 'sovereigns_welcome',
    system: 'shrine',
    row: 6,
    name: "Sovereign's Welcome",
    desc: 'Begin every descent with a free relic choice.',
    ranks: 1,
    costs: [350],
    effect: 'freeStartRelic',
  },
  {
    id: 'warden_ascendant',
    system: 'shrine',
    row: 6,
    name: 'Warden Ascendant',
    desc: 'Capstone: +10 max HP, +5% damage, +1 Emberburst carried.',
    ranks: 1,
    costs: [500],
    effect: 'ascendant',
    params: { hp: 10, dmg: 0.05, potionCap: 1 },
  },
]

// ---------------------------------------------------------------------------
// The Provisioner. Crests only. Deed-gated stock stays VISIBLE with its deed
// named (RoR2 logbook style) — the gate is information, not a secret.
//
// Provisioner's Stock I/II name the relic ids they add to the in-run pool.
// RelicDef has no tier field (types.ts is frozen), so the pool gate is this id
// list: every relic named here is withheld from the base pool until its stock
// item is bought. game/meta.ts derives the base pool by subtraction.

export const PROVISIONER_STOCK: UnlockNode[] = [
  {
    id: 'whisper_bow',
    system: 'provisioner',
    name: 'Whisper Bow',
    desc: 'A hunting form: fast, piercing, unkind at range.',
    ranks: 1,
    costs: [2],
    effect: 'weapon',
    params: { weapon: 'whisperbow' },
  },
  {
    id: 'storm_staff',
    system: 'provisioner',
    name: 'Storm Staff',
    desc: 'Arcing splash bolts and a growing thunderhead.',
    ranks: 1,
    costs: [4],
    effect: 'weapon',
    params: { weapon: 'stormstaff' },
  },
  {
    id: 'bulwark_maul',
    system: 'provisioner',
    name: 'Bulwark Maul',
    desc: 'Slow, staggering, and briefly unbreakable.',
    ranks: 1,
    costs: [6],
    deedGate: 'boss2_kill',
    effect: 'weapon',
    params: { weapon: 'bulwarkmaul' },
  },
  {
    id: 'ember_charm',
    system: 'provisioner',
    name: 'Ember Charm',
    desc: 'Talisman: +10% embers.',
    ranks: 1,
    costs: [2],
    effect: 'talEmberCharm',
    params: { add: 0.1 },
  },
  {
    id: 'warding_knot',
    system: 'provisioner',
    name: 'Warding Knot',
    desc: 'Talisman: the first hit taken in each room is absorbed.',
    ranks: 1,
    costs: [3],
    effect: 'talWardingKnot',
  },
  {
    id: 'tide_anchor',
    system: 'provisioner',
    name: 'Tide Anchor',
    desc: 'Talisman: immune to pulls and knockback.',
    ranks: 1,
    costs: [3],
    deedGate: 'boss2_nohit',
    effect: 'talTideAnchor',
  },
  {
    id: 'grave_candle',
    system: 'provisioner',
    name: 'Grave Candle',
    desc: 'Talisman: the first husk in each room seals itself.',
    ranks: 1,
    costs: [3],
    deedGate: 'husks_sealed',
    effect: 'talGraveCandle',
  },
  {
    id: 'coin_feather',
    system: 'provisioner',
    name: 'Coin Feather',
    desc: 'Talisman: gold leans your way, and tolls cost 25% less.',
    ranks: 1,
    costs: [3],
    effect: 'talCoinFeather',
    params: { radius: 210, tollMul: 0.75 },
  },
  {
    id: 'stock_i',
    system: 'provisioner',
    name: "Provisioner's Stock I",
    desc: 'Six more relics enter the in-run pool.',
    ranks: 1,
    costs: [4],
    effect: 'relicPool',
    params: {
      relics: 'quickstep,coin_reek,ember_keeper,gravity_of_gold,moth_eaten_cloak,widows_flask',
    },
  },
  {
    id: 'stock_ii',
    system: 'provisioner',
    name: "Provisioner's Stock II",
    desc: 'Six rarer relics enter the in-run pool.',
    ranks: 1,
    costs: [6],
    effect: 'relicPool',
    params: {
      relics: 'appraisers_eye,gluttons_hearth,quickened_sigil,sepulchre_tithe,diviners_thread,hunters_mark',
    },
  },
]

// --------------------------------------------------- trivial table builders

export const ALL_UNLOCKS: UnlockNode[] = [...SHRINE_NODES, ...PROVISIONER_STOCK]

export const UNLOCK_BY_ID: Map<string, UnlockNode> = new Map(ALL_UNLOCKS.map((n) => [n.id, n]))

/** SHRINE_ROWS[0] is row 1. */
export const SHRINE_ROWS: UnlockNode[][] = [1, 2, 3, 4, 5, 6].map((r) =>
  SHRINE_NODES.filter((n) => n.row === r),
)

/** Talisman stock ids, in rack order. */
export const TALISMAN_IDS: string[] = PROVISIONER_STOCK.filter((n) =>
  n.effect.startsWith('tal'),
).map((n) => n.id)

/** Weapon stock: weapon form id -> the provisioner node that sells it. */
export const WEAPON_STOCK: Map<string, UnlockNode> = new Map(
  PROVISIONER_STOCK.filter((n) => n.effect === 'weapon').map((n) => [String(n.params?.weapon ?? ''), n]),
)

/** Relic ids withheld from the base pool until their stock item is bought. */
export const STOCK_GATED_RELICS: string[] = PROVISIONER_STOCK.filter(
  (n) => n.effect === 'relicPool',
).flatMap((n) => String(n.params?.relics ?? '').split(',').filter(Boolean))
